<script setup lang="ts">
/**
 * Journal d'activités (Lot 7) — onglet vertical accroché au bord DROIT de la
 * page, présent dans toutes les sections. Un clic ouvre le panneau latéral qui
 * affiche le flux temps réel selon le contexte :
 *   - liste  → toute la section (soumissions / réclamations)
 *   - fiche  → l'enregistrement précis (soumission ou dossier), avec le détail
 *              champ par champ de chaque modification.
 * Monté une seule fois dans App.vue.
 */
import { ref, computed, watch, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useActivityStore } from "../stores/activity";
import { useI18n } from "../composables/useI18n";

const route = useRoute();
const activity = useActivityStore();
const { t, locale } = useI18n();

const open = ref(false);

/** Contexte (section + éventuel enregistrement) déduit de la route. */
const scope = computed<{ section?: string; targetId?: string }>(() => {
  const n = route.name as string;
  const id = route.params.id as string | undefined;
  switch (n) {
    case "quotes": return { section: "soumissions" };
    case "quote-editor": return { section: "soumissions", targetId: id };
    case "quote-new": return { section: "soumissions" };
    case "reclamations": return { section: "reclamations" };
    case "reclamation-editor": return { section: "reclamations", targetId: id };
    case "reclamation-new": return { section: "reclamations" };
    default: return {}; // flux global récent
  }
});

// (Ré)abonner quand le panneau est ouvert et que le contexte change.
watch(
  () => [open.value, scope.value.section, scope.value.targetId] as const,
  ([isOpen, section, targetId]) => {
    if (isOpen) activity.subscribeScope(section, targetId);
  },
);

function toggle() {
  open.value = !open.value;
  if (open.value) activity.subscribeScope(scope.value.section, scope.value.targetId);
}

onUnmounted(() => activity.unsubscribe());

function authorLabel(email?: string): string {
  return email ? email.split("@")[0] : "?";
}

function toMs(at: any): number {
  if (!at) return 0;
  if (at.toMillis) return at.toMillis();
  if (at.seconds) return at.seconds * 1000;
  return 0;
}

/**
 * Regroupe les opérations faites « en même temps » : entrées consécutives du
 * même auteur, même action, même fiche, dans une fenêtre de 20 s → une seule
 * carte multi-lignes. Les détails sont dédupliqués par champ (on garde la
 * valeur la plus récente).
 */
const GROUP_WINDOW_MS = 20000;
const groupedEntries = computed(() => {
  const groups: any[] = [];
  for (const e of activity.entries) {
    const last = groups[groups.length - 1];
    if (
      last &&
      last.actorUid === e.actorUid &&
      last.action === e.action &&
      (last.targetId || "") === (e.targetId || "") &&
      Math.abs(last._oldestMs - toMs(e.at)) <= GROUP_WINDOW_MS
    ) {
      last.details.push(...(e.details || []));
      last._oldestMs = toMs(e.at);
    } else {
      groups.push({
        id: e.id,
        actorUid: e.actorUid,
        actorEmail: e.actorEmail,
        action: e.action,
        label: e.label,
        targetId: e.targetId,
        at: e.at,
        _oldestMs: toMs(e.at),
        details: [...(e.details || [])],
      });
    }
  }
  // Dédup par champ : on garde la 1re occurrence (la plus récente, tri desc).
  for (const g of groups) {
    const seen = new Set<string>();
    g.details = g.details.filter((d: string) => {
      const field = splitDetail(d).field;
      if (seen.has(field)) return false;
      seen.add(field);
      return true;
    });
  }
  return groups;
});
/** Sépare "Champ : valeur" pour mettre le nom du champ en gras. */
function splitDetail(d: string): { field: string; value: string } {
  const idx = d.indexOf(" : ");
  if (idx === -1) return { field: d, value: "" };
  return { field: d.slice(0, idx), value: d.slice(idx + 3) };
}
function actionLabel(action: string): string {
  return t(`activity.action.${action}`) || action;
}
function relTime(at: any): string {
  const ms = at?.toMillis ? at.toMillis() : (at?.seconds ? at.seconds * 1000 : null);
  if (!ms) return "";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return locale.value === "fr" ? "à l'instant" : "just now";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} j`;
  try { return new Date(ms).toLocaleDateString(); } catch { return ""; }
}

const scopeHint = computed(() => {
  if (scope.value.targetId) return locale.value === "fr" ? "cette fiche" : "this record";
  if (scope.value.section) return scope.value.section;
  return locale.value === "fr" ? "tout" : "all";
});
</script>

<template>
  <!-- Onglet vertical accroché au bord droit -->
  <button v-if="!open" class="activity-tab" @click="toggle" :title="t('activity.title')">
    <i class="ti ti-history"></i>
    <span class="tab-text">{{ t("activity.title") }}</span>
  </button>

  <!-- Panneau -->
  <div v-if="open" class="activity-overlay" @click.self="open = false">
    <aside class="activity-drawer">
      <header class="drawer-head">
        <span>
          <i class="ti ti-history"></i> {{ t("activity.title") }}
          <small class="scope-hint">· {{ scopeHint }}</small>
        </span>
        <button class="drawer-close" @click="open = false" aria-label="close">
          <i class="ti ti-x"></i>
        </button>
      </header>

      <div v-if="activity.loading" class="drawer-loading">{{ t("common.loading") }}</div>
      <div v-else-if="activity.entries.length === 0" class="drawer-empty">{{ t("activity.empty") }}</div>
      <ul v-else class="activity-list">
        <li v-for="g in groupedEntries" :key="g.id" class="activity-item">
          <div class="activity-line">
            <span class="activity-actor">{{ authorLabel(g.actorEmail) }}</span>
            <span class="activity-action" :class="g.action">{{ actionLabel(g.action) }}</span>
          </div>
          <div class="activity-label">{{ g.label }}</div>
          <ul v-if="g.details && g.details.length" class="activity-details">
            <li v-for="(d, i) in g.details" :key="i">
              <strong class="detail-field">{{ splitDetail(d).field }}</strong>
              <span v-if="splitDetail(d).value"> : {{ splitDetail(d).value }}</span>
            </li>
          </ul>
          <div class="activity-time">{{ relTime(g.at) }}</div>
        </li>
      </ul>
    </aside>
  </div>
</template>

<style scoped>
/* Onglet vertical bord droit */
.activity-tab {
  position: fixed; top: 140px; right: 0; z-index: 900;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 12px 6px;
  background: var(--surface, #14181f);
  border: 1px solid var(--border, #2a2f3a); border-right: none;
  border-radius: 8px 0 0 8px;
  color: var(--text-muted, #8a94a6); cursor: pointer;
  box-shadow: -2px 0 8px rgba(0,0,0,0.15);
}
.activity-tab:hover { color: var(--accent, #4facfe); border-color: var(--accent, #4facfe); }
.activity-tab .tab-text {
  writing-mode: vertical-rl; text-orientation: mixed;
  font-size: 12px; letter-spacing: 0.5px;
}

.activity-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, 0.35);
  display: flex; justify-content: flex-end;
}
.activity-drawer {
  width: min(380px, 92vw); height: 100%;
  background: var(--surface, #14181f);
  border-left: 1px solid var(--border, #2a2f3a);
  display: flex; flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.3);
}
.drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid var(--border, #2a2f3a);
  font-size: 15px; font-weight: 600; color: var(--text, #e8ebf0);
}
.scope-hint { color: var(--text-dim, #5b6578); font-weight: 400; font-size: 12px; }
.drawer-close {
  background: transparent; border: none; color: var(--text-muted, #8a94a6);
  font-size: 18px; cursor: pointer;
}
.drawer-loading, .drawer-empty {
  padding: 24px 16px; color: var(--text-muted, #8a94a6); font-size: 13px;
}
.activity-list { list-style: none; margin: 0; padding: 8px 0; overflow-y: auto; }
.activity-item { padding: 10px 16px; border-bottom: 1px solid var(--border, #22262e); }
.activity-line { display: flex; align-items: center; gap: 6px; }
.activity-actor { font-size: 13px; font-weight: 600; color: var(--text, #e8ebf0); }
.activity-action {
  font-size: 11px; padding: 1px 8px; border-radius: 10px;
  background: var(--surface2, #1d2129); color: var(--text-muted, #8a94a6);
}
.activity-action.create { background: rgba(74,222,128,0.15); color: #4ade80; }
.activity-action.delete { background: rgba(255,138,138,0.15); color: #ff8a8a; }
.activity-action.finished { background: rgba(79,172,254,0.15); color: var(--accent,#4facfe); }
.activity-label { font-size: 13px; color: var(--text-muted, #8a94a6); margin-top: 2px; }
.activity-details {
  list-style: none; margin: 4px 0 0; padding: 6px 10px;
  background: var(--surface2, #1d2129); border-radius: 6px;
}
.activity-details li { font-size: 12px; color: var(--text-muted, #8a94a6); line-height: 1.5; }
.detail-field { color: var(--text, #e8ebf0); font-weight: 600; }
.activity-time { font-size: 11px; color: var(--text-dim, #5b6578); margin-top: 4px; }
</style>
